import { useMemo, useState } from 'react';

import { useParams } from 'react-router-dom';
import { toast } from 'sonner';

import { SearchableTemplateList, TemplateList, TemplateListItem } from './template-list';
import { WEXLER_PROMPT_TEMPLATES } from './wexler-prompt-templates';
import { OrgPermission } from '../../../../../../../../../../backend/src/organisation/organisation-permission';
import { useDeletePromptTemplate } from '@/api/mutations/useDeletePromptTemplate';
import { useGetPromptTemplates } from '@/api/queries/useGetPromptTemplates';
import { useOrgPermissions } from '@/hooks/orgPermissions';
import { ConfirmationModal } from '@/new/components/dialogs/confirmation-dialog';
import { Icon } from '@/new/components/icon';
import { NewChip } from '@/new/components/new-chip';
import { Button } from '@/new/components/shadcn/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/new/components/shadcn/popover';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/new/components/shadcn/tabs';

interface TemplatesPopoverProps {
  onSelect: (body: string) => void;
  onOpenTemplateBuilder: () => void;
  onEditTemplate: (template: { id: string; title: string; body: string }) => void;
}

export function TemplatesPopover({ onSelect, onOpenTemplateBuilder, onEditTemplate }: TemplatesPopoverProps) {
  const { matterId } = useParams();
  const { isEnabled } = useOrgPermissions();
  const isTemplateBuilderEnabled = isEnabled(OrgPermission.KimTemplateBuilder);
  const { data: userTemplates = [] } = useGetPromptTemplates({
    matterId,
    enabled: isTemplateBuilderEnabled,
  });
  const { mutate: deleteTemplate, isLoading: isDeleting } = useDeletePromptTemplate(matterId);
  const [deletingTemplate, setDeletingTemplate] = useState<TemplateListItem | null>(null);

  const wexlerTemplateItems = useMemo<TemplateListItem[]>(
    () => WEXLER_PROMPT_TEMPLATES.map((t) => ({ id: t.id, label: t.label, icon: t.icon, body: t.template })),
    [],
  );

  const personalTemplateItems = useMemo<TemplateListItem[]>(
    () => userTemplates.map((t) => ({ id: t.id, label: t.title, body: t.body })),
    [userTemplates],
  );

  const handleConfirmDelete = () => {
    if (!deletingTemplate) return;
    deleteTemplate(deletingTemplate.id, {
      onSuccess: () => setDeletingTemplate(null),
      onError: (error) => {
        toast.error(error instanceof Error ? error.message : 'Error deleting template. Please try again.');
      },
    });
  };

  return (
    <>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant={'ghost'} size={'sm'}>
            {isTemplateBuilderEnabled && <NewChip className="relative top-auto right-auto" />}
            Templates
            <Icon className="lucide--chevron-down" />
          </Button>
        </PopoverTrigger>
        <PopoverContent
          align="end"
          className="-mt-1 -mr-2 w-sm max-w-none p-0"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <Tabs defaultValue="wexler">
            <TabsList className="h-8 w-full justify-start gap-1 p-5 px-3">
              <TabsTrigger value="wexler" className="px-2 py-3 pb-2 text-[13px]">
                Wexler
              </TabsTrigger>
              {isTemplateBuilderEnabled && (
                <>
                  <TabsTrigger value="personal" className="px-2 py-3 pb-2 text-[13px]">
                    Personal
                  </TabsTrigger>
                </>
              )}
            </TabsList>

            <TabsContent value="wexler" className="m-0 max-h-80 overflow-y-auto">
              <TemplateList templates={wexlerTemplateItems} emptyMessage="No Wexler templates" onSelect={onSelect} />
            </TabsContent>

            {isTemplateBuilderEnabled && (
              <>
                <TabsContent value="personal" className="m-0 max-h-80 overflow-y-auto">
                  <SearchableTemplateList
                    templates={personalTemplateItems}
                    emptyMessage="No personal templates in this Matter"
                    onSelect={onSelect}
                    onEdit={(t) => onEditTemplate({ id: t.id, title: t.label, body: t.body })}
                    onDelete={(t) => setDeletingTemplate(t)}
                  />
                </TabsContent>
              </>
            )}
          </Tabs>

          {isTemplateBuilderEnabled && (
            <div>
              <hr />
              <button
                onClick={onOpenTemplateBuilder}
                className="m-1.5 flex w-full items-center gap-2 rounded-sm px-2 py-1.25 text-left text-[13px] text-gray-800 duration-150 hover:bg-gray-50"
              >
                <Icon className="lucide--plus" /> Build a new template
              </button>
            </div>
          )}
        </PopoverContent>
      </Popover>

      <ConfirmationModal
        isOpen={deletingTemplate !== null}
        onOpenChange={(open) => {
          if (!open) setDeletingTemplate(null);
        }}
        title="Delete template"
        isLoading={isDeleting}
        onConfirm={handleConfirmDelete}
      >
        Are you sure you want to delete <strong>{deletingTemplate?.label}</strong>? This cannot be undone.
      </ConfirmationModal>
    </>
  );
}
